import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Scan, Loader2, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";

const SIDE_IMG =
  "https://images.unsplash.com/photo-1666214280352-db292c05fd80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxtcmklMjBzY2FufGVufDB8fHx8MTc4OTA3MTg4Nnww&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink grid grid-cols-1 lg:grid-cols-2" data-testid="register-page">
      <div className="flex flex-col justify-center px-6 md:px-16 lg:px-24 py-16 order-2 lg:order-1">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-md mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-mute hover:text-slate-900 transition-colors duration-300 mb-12" data-testid="register-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </Link>

          <div className="flex items-center gap-2 mb-8">
            <span className="w-9 h-9 bg-brand rounded-lg flex items-center justify-center">
              <Scan className="w-4.5 h-4.5 text-white" />
            </span>
            <span className="font-display font-extrabold tracking-tight text-xl">X-GEN.AI</span>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">Request access</h1>
          <p className="text-mute text-sm mb-10">Create your clinician account and explore the diagnostic dashboard.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="register-form">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute block mb-2" htmlFor="register-name">Full name</label>
              <Input id="register-name" data-testid="register-name-input" value={form.name} onChange={set("name")} placeholder="Dr. Jane Carter" required className="bg-surface border-slate-200 rounded-lg h-12 focus-visible:ring-brand" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute block mb-2" htmlFor="register-email">Work email</label>
              <Input id="register-email" data-testid="register-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@hospital.org" required className="bg-surface border-slate-200 rounded-lg h-12 focus-visible:ring-brand" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute block mb-2" htmlFor="register-password">Password</label>
              <Input id="register-password" data-testid="register-password-input" type="password" value={form.password} onChange={set("password")} placeholder="Minimum 8 characters" required minLength={8} className="bg-surface border-slate-200 rounded-lg h-12 focus-visible:ring-brand" />
            </div>

            {error && (
              <p className="text-pulse text-sm font-mono" data-testid="register-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="register-form-submit-button"
              className="w-full flex items-center justify-center gap-2 bg-brand text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 hover:bg-[#0F172A] hover:text-white transition-colors duration-300 disabled:opacity-50"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Creating account..." : "Create account"}
            </button>
          </form>

          <p className="mt-8 text-sm text-mute">
            Already have access?{" "}
            <Link to="/login" className="text-brand hover:text-[#0F172A] transition-colors duration-300" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>

      <div className="hidden lg:block relative overflow-hidden border-l border-slate-200 order-1 lg:order-2">
        <img src={SIDE_IMG} alt="MRI suite" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/60" aria-hidden="true" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand mb-4">X-GEN.AI</p>
          <p className="font-display text-3xl font-black uppercase tracking-tighter leading-tight">
            Diagnosis at
            <br />
            machine speed.
          </p>
        </div>
      </div>
    </div>
  );
}
